import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { InvoiceSection } from './schema';

type UseInvoiceSectionsResult = {
  invoiceSections: InvoiceSection[];
  loading: boolean;
  error: string | undefined;
};

type InvoiceSectionsResponse = {
  billingProfileId: string;
  invoiceSections: InvoiceSection[];
  invoiceSectionCount: number;
};

export function useInvoiceSections(
  billingProfileId: string,
): UseInvoiceSectionsResult {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [invoiceSections, setInvoiceSections] = useState<InvoiceSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!billingProfileId) {
      setInvoiceSections([]);
      return undefined;
    }

    const abortController = new AbortController();
    let isActive = true;

    const fetchSections = async () => {
      setLoading(true);
      setError(undefined);

      try {
        const baseUrl = await discoveryApi.getBaseUrl('azure-plugin');
        const { token } = await identityApi.getCredentials();
        const url = new URL(`${baseUrl}/billing/invoice-sections`);
        url.searchParams.set('billingProfileId', billingProfileId);

        const response = await fetch(url.toString(), {
          signal: abortController.signal,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch invoice sections: ${response.status} ${response.statusText}`,
          );
        }

        const data = (await response.json()) as InvoiceSectionsResponse;

        if (isActive) {
          setInvoiceSections(data.invoiceSections ?? []);
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (isActive) {
          setError('Unable to load invoice sections. Please try again.');
          setInvoiceSections([]);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchSections();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [billingProfileId, discoveryApi, identityApi]);

  return { invoiceSections, loading, error };
}
